import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { fetchScene, SceneDetail } from '../queries';
import { createActionRequest, fetchPlaces } from '../actionQueries';
import { AttachedActionSubmissionGuard } from '../actionSubmissionGuard';
import { useDetachedActionIds } from '../useDetachedActionIds';
import { SceneHeader } from '../components/SceneHeader';
import { SceneInteractionPanel } from '../components/SceneInteractionPanel';
import { GMPromptQueue } from '../components/GMPromptQueue';
import { ActionPanel } from '../components/ActionPanel';
import { PlaceBar } from '../components/PlaceBar';
import { TavernGameWidget } from '../components/TavernGameWidget';
import { SpeakerQueueBar } from '../components/SpeakerQueueBar';
import { SceneTacticalMap } from '../components/SceneTacticalMap';
import { HighlightReel } from '../components/HighlightReel';
import { ConsentPrompt } from '../components/ConsentPrompt';
import { PrecapturePanel } from '../components/PrecapturePanel';
import { SceneLinesAndVeilsCard } from '@/boundaries/components/SceneLinesAndVeilsCard';
import { SineatingInbox } from '@/magic/components/SineatingInbox';
import { SoulTetherRescuePrompt } from '@/magic/components/SoulTetherRescuePrompt';
import { EntryFlourishOfferGate } from '@/magic/components/EntryFlourishOfferGate';
import { WitnessReactionOfferGate } from '@/justice/components/WitnessReactionOfferGate';
import { CommandInput } from '@/game/components/CommandInput';
import type { ComposerMode } from '@/game/components/CommandInput';
import { CharacterCardDrawer } from '@/game/components/CharacterCardDrawer';
import { PersonaCardContext } from '@/game/persona-menu/PersonaCardContext';
import type { PoseUnitAvatarClickPersona } from '../components/PoseUnit';
import type { ActionAttachmentInfo } from '../actionTypes';
import { useAppSelector } from '@/store/hooks';
import { actingPersonaId } from '@/roster/persona';
import { useMyRosterEntriesQuery } from '@/roster/queries';
import { PendingActionAttachments } from '../components/PendingActionAttachments';
import { usePendingUnlinkedActions } from '../hooks/usePendingUnlinkedActions';
import { useBattleForSceneQuery } from '@/battles/queries';
import { RitualProposedChip } from '@/rituals/components/RitualProposedChip';
import { useEncounterForScene } from '@/combat/queries';
import { CombatRail } from '@/combat/components/CombatRail';
import { GMEncounterControls } from '@/combat/sections/GMEncounterControls';
import { GMStoryRail } from '../components/GMStoryRail';
import { ScenarioCard } from '../components/ScenarioCard';
import { LinkedStoriesPanel } from '@/crossover/components/LinkedStoriesPanel';
import {
  GMAdjudicationPanel,
  GM_TOOL_TABS,
  NON_COMBAT_GM_TOOL_TABS,
} from '../components/GMAdjudicationPanel';
import { SelfCheckPanel } from '../components/SelfCheckPanel';
import { CheckCallPromptCard } from '../components/CheckCallPromptCard';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

export function SceneDetailPage() {
  const { id = '' } = useParams();
  const { data: scene, refetch } = useQuery<SceneDetail>({
    queryKey: ['scene', id],
    queryFn: () => fetchScene(id),
    refetchInterval: (query) => (query.state.data?.is_active ? 60000 : false),
  });

  const isActive = scene?.is_active ?? false;
  const roomName = scene?.name ?? 'Room';
  const activeCharacter = useAppSelector((state) => state.game.active);
  // #3760 Task 12 review fix — this page previously passed no `ready` prop to
  // `CommandInput` at all, silently taking its `ready = true` default, which
  // never changes: there is no `useGameSocket` reference on this page to flip
  // it. That made a reconnect mid-send invisible here (CommandInput's
  // markUnknown-on-reconnect effect keys off a `ready` false -> true
  // transition) even though this composer dispatches say/whisper/tt through
  // the exact same `executeAction`/`pendingSpeechRef` path `GameWindow` uses.
  // `state.game.sessions[character].isConnected` is the same global signal
  // `GameWindow.tsx` reads (`useGameSocket`'s module-level socket/session
  // bookkeeping and the Redux session state it dispatches into are shared
  // across the whole app, not owned by whichever page happens to be mounted
  // — `ConsentAttentionNotifier.tsx` already calls `connect()` from outside
  // `GamePage` on that same assumption), so reading it here (rather than
  // duplicating `GameWindow`'s own connection management) is the correct fix.
  // Deliberately omits `GameWindow`'s extra `Boolean(session.room)` clause —
  // that gates on the ACTIVE character's freeform room re-entering, which
  // this page's scene-scoped composer doesn't depend on.
  //
  // Final-review Finding 1 fix — a session entry only exists in
  // `state.game.sessions` AFTER `connect()` has been called somewhere in the
  // app, and nothing on `/scenes/:id` ever calls it (only
  // GamePage/GameWindow/GameTopBar do, all mounted only inside `/game`). So
  // on a fresh load of this page (bookmark, direct link, reload) `session` is
  // `undefined` — that means "this page isn't using a socket right now," not
  // "disconnected," and must not permanently disable the composer (it never
  // recovers, since nothing here ever connects one). Distinguish the two:
  // no session at all -> the REST/executeAction paths this composer uses
  // don't depend on the socket being up, so `ready` defaults to `true`; a
  // session that DOES exist here (e.g. because `GameWindow` is also mounted
  // for the same account, or a future caller connects one) still gates on
  // its own `isConnected`, preserving Task 12's reconnect-detection logic.
  const session = useAppSelector((state) => state.game.sessions?.[activeCharacter ?? '']);
  const isConnected = session ? session.isConnected : true;

  // Combat rail fold-in (#2197): combat now renders inline on the scene page
  // instead of a separate /scenes/:id/combat route — the fight never leaves
  // the room it's happening in. Two-column layout (matching the former
  // CombatScenePage's C-frame grid) only while an active encounter exists;
  // single column otherwise.
  const sceneIdNum = id ? Number(id) : 0;
  const {
    data: encounterListItem,
    encounters: activeEncounterList = [],
    isLoading: encounterLoading,
  } = useEncounterForScene(sceneIdNum);
  const [selectedEncounterId, setSelectedEncounterId] = useState(0);
  useEffect(() => {
    if (selectedEncounterId > 0 && activeEncounterList.some((e) => e.id === selectedEncounterId))
      return;
    setSelectedEncounterId(activeEncounterList[0]?.id ?? encounterListItem?.id ?? 0);
  }, [activeEncounterList, encounterListItem?.id, selectedEncounterId]);
  const selectedEncounter =
    activeEncounterList.find((e) => e.id === selectedEncounterId) ?? encounterListItem;
  const hasActiveEncounter = !encounterLoading && selectedEncounter != null;
  const encounterId = selectedEncounter?.id ?? 0;
  // The scene's active-encounter list poll drops a completed encounter within
  // 15s (useEncounterForScene), but the outcome banner and aftermath digest
  // have to outlive that drop until the player dismisses them, otherwise the
  // rail vanishes out from under whoever is still reading it (#3551).
  // lingeringEncounterId remembers the last real encounterId and keeps the
  // rail mounted on it until CombatRail's onDismissOutcome fires.
  const [lingeringEncounterId, setLingeringEncounterId] = useState(0);
  // Dismissing the outcome banner before the poll drops the completed encounter
  // (hasActiveEncounter still true) must hide the rail immediately rather than
  // waiting up to 15s for the next poll (#3551 minor 4): the rail shows only
  // while railEncounterId hasn't been dismissed; a new encounter gets a new id,
  // so it reappears on its own.
  const [dismissedEncounterId, setDismissedEncounterId] = useState(0);
  // A route change (e.g. /scenes/1 -> /scenes/2) re-renders SceneDetailPage in
  // place rather than remounting it (no `key` on the route), so scene 1's
  // lingering/dismissed encounter state would otherwise survive onto scene 2's
  // rail (#3551 important 1). One effect, keyed on both scene and encounter id,
  // so the scene-change reset and the fresh-encounter set can never race: on a
  // scene change the lingering/dismissed ids reset first, then (same pass) pick
  // up the new scene's own active encounter if it already has one.
  const prevSceneIdRef = useRef(sceneIdNum);
  useEffect(() => {
    if (prevSceneIdRef.current !== sceneIdNum) {
      prevSceneIdRef.current = sceneIdNum;
      setLingeringEncounterId(encounterId > 0 ? encounterId : 0);
      setDismissedEncounterId(0);
      return;
    }
    if (encounterId > 0) {
      setLingeringEncounterId(encounterId);
    }
  }, [sceneIdNum, encounterId]);
  const railEncounterId = encounterId || lingeringEncounterId;
  const showCombatRail = railEncounterId > 0 && railEncounterId !== dismissedEncounterId;
  const encounterChooser =
    activeEncounterList.length > 1 ? (
      <div className="mb-2 flex flex-wrap gap-1" data-testid="encounter-chooser">
        <span className="self-center px-1 text-xs text-muted-foreground">Encounters:</span>
        {activeEncounterList.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setSelectedEncounterId(entry.id)}
            className={cn(
              'rounded border px-2 py-1 text-xs',
              entry.id === encounterId
                ? 'border-primary bg-primary/10 font-semibold'
                : 'border-border'
            )}
            aria-pressed={entry.id === encounterId}
          >
            #{entry.id} · {(entry.encounter_type ?? 'encounter').replace(/_/g, ' ')}
          </button>
        ))}
      </div>
    ) : null;
  const handleDismissOutcome = useCallback(() => {
    setDismissedEncounterId(railEncounterId);
    setLingeringEncounterId(0);
  }, [railEncounterId]);
  // GM story rail fold-in (#3434): shares the right-rail column with the
  // combat rail. Mounted whenever the viewer can GM this scene at all --
  // GMStoryRail itself renders the "no beat running" fallback when
  // scene.running_beat is null, so the grid doesn't collapse the moment a
  // GM's beat finishes.
  const showStoryRail = !!scene?.viewer_can_gm;

  // Scroll the rail into view the moment an encounter first appears
  // (none -> active transition) so a player mid-pose notices combat starting.
  const railRef = useRef<HTMLDivElement>(null);
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (hasActiveEncounter && !wasActiveRef.current) {
      railRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    wasActiveRef.current = hasActiveEncounter;
  }, [hasActiveEncounter]);
  const { data: battle } = useBattleForSceneQuery(id ? Number(id) : null);

  // Resolve the active character's primary persona id for submit_pose REST calls.
  // Also derives characterSheetId: CharacterSheet uses OneToOneField(primary_key=True)
  // to ObjectDB, so character_id === character_sheet pk.
  const { data: myRosterEntries = [] } = useMyRosterEntriesQuery();
  const activeEntry = useMemo(
    () => myRosterEntries.find((e) => e.name === activeCharacter) ?? null,
    [myRosterEntries, activeCharacter]
  );
  const personaId = actingPersonaId(activeEntry);
  const characterSheetId = activeEntry?.character_id ?? 0;
  // The active character's own RosterEntry id (#2156 Task 7) — the FriendButton's
  // `viewerEntryId` inside the character-card drawer.
  const viewerEntryId = activeEntry?.id ?? null;

  // Track IDs the user has detached from the auto-attach chip strip.
  const { detachedActionIds, handleDetach, handleUndoDetach, handlePoseSubmitted } =
    useDetachedActionIds();

  // Pending unlinked actions for the chip strip.
  const { data: pendingActions } = usePendingUnlinkedActions(id, personaId);
  const pendingActionIds = useMemo(() => pendingActions.map((a) => a.id), [pendingActions]);

  const [composerMode, setComposerMode] = useState<ComposerMode>({
    command: 'pose',
    targets: [],
    label: `Pose → Room`,
  });

  const [targetToAppend, setPendingTarget] = useState<string | null>(null);
  const [actionAttachment, setActionAttachment] = useState<ActionAttachmentInfo | null>(null);
  const queryClient = useQueryClient();

  const submitAction = useMutation({
    mutationFn: ({ action }: { action: ActionAttachmentInfo; clientRequestId: string }) =>
      createActionRequest(id, {
        action_key: action.actionKey,
        target_persona_id: action.targetPersonaId,
        technique_id: action.techniqueId,
      }),
    onSuccess: (_result, variables) => {
      attachedActionGuard.current.succeed(variables.clientRequestId);
      setActionAttachment(null);
      // 2026-07 audit: 'scene-messages' matched no query — the feed key is 'scene-interactions'.
      queryClient.invalidateQueries({ queryKey: ['scene-interactions', id] });
      queryClient.invalidateQueries({ queryKey: ['pending-requests', id] });
    },
    onError: (_error, variables) => {
      attachedActionGuard.current.fail(variables.clientRequestId);
      // Keep the attachment so user can retry
    },
  });

  const attachedActionGuard = useRef(new AttachedActionSubmissionGuard());

  const handleSubmitAction = useCallback(
    (action: ActionAttachmentInfo, clientRequestId?: string) => {
      const correlationId =
        clientRequestId ?? `${action.actionKey}:${action.targetPersonaId ?? ''}`;
      if (!attachedActionGuard.current.start(correlationId)) return;
      submitAction.mutate({ action, clientRequestId: correlationId });
    },
    [submitAction]
  );

  const handleTargetConsumed = useCallback(() => {
    setPendingTarget(null);
  }, []);

  const handleActionAttach = useCallback((action: ActionAttachmentInfo) => {
    setActionAttachment(action);
  }, []);

  const handleActionDetach = useCallback(() => {
    setActionAttachment(null);
  }, []);

  // Update the default label when scene name loads
  const handleComposerModeChange = useCallback((mode: ComposerMode) => {
    setComposerMode(mode);
  }, []);

  // Character-card drawer (#2156 Task 7): the clicked bubble's persona identity,
  // or null when the drawer is closed. Mirrors GamePage's state — the drawer
  // opens "in place" over this record page's feed, not as a route navigation.
  const [cardPersona, setCardPersona] = useState<PoseUnitAvatarClickPersona | null>(null);
  // #4030 fix round 1: lets PersonaMenu's "View sheet" (rendered inside
  // PoseUnit here, same as on /game) open this page's own character-card
  // drawer instead of silently no-oping — mirrors GamePage.tsx's provider.
  const cardContextValue = useMemo(() => ({ openCharacterCard: setCardPersona }), []);
  const handleWhisper = useCallback(
    (name: string) => {
      handleComposerModeChange({ command: 'whisper', targets: [name], label: `Whisper → ${name}` });
      setCardPersona(null);
    },
    [handleComposerModeChange]
  );

  // `isAtPlace` (#2156, Task 6): derived from the SAME `['scene-places',
  // placesRoomId]` query key `PlaceBar` uses below, so React Query dedupes the
  // two fetches into one (query-reuse, matching GamePage's approach).
  // `fetchPlaces` filters `?room=<id>` — a ROOM id, not the scene id — so this
  // derives the room id from `scene.location.id` (fold-in fix, #2156: the
  // earlier version passed the *scene* id here and to `PlaceBar`, which only
  // worked by coincidence when scene pk === room pk).
  const placesRoomId = scene?.location?.id != null ? String(scene.location.id) : undefined;
  const { data: placesData } = useQuery({
    queryKey: ['scene-places', placesRoomId],
    queryFn: () => fetchPlaces(placesRoomId!),
    enabled: !!placesRoomId,
  });
  // #3760 Task 10 fix — `currentPlace` (not just the boolean) is threaded down to
  // CommandInput so tt (tabletalk) can dispatch via executeAction with a real
  // place kwarg, mirroring GamePage.tsx's identical fix.
  const currentPlace = placesData?.results?.find((place) => place.viewer_is_present);
  const isAtPlace = !!currentPlace;

  // The foldable part of the header (#3557): rendered inline when idle, inside
  // the "Scene tools" accordion during an encounter. Same order as before.
  // Folding remounts the subtree, so a local draft in SelfCheckPanel or
  // TavernGameWidget is lost at the tick an encounter starts; accepted in
  // ADR-0272. Prompts that need an answer never fold.
  const sceneTools = (
    <>
      {scene && <SceneLinesAndVeilsCard sceneId={id} />}
      {placesRoomId && <PlaceBar sceneId={placesRoomId} />}
      {placesRoomId && <TavernGameWidget roomId={placesRoomId} />}
      {placesRoomId && <SpeakerQueueBar roomId={placesRoomId} />}
      {/* One map during a fight: the rail's CombatTacticalMap draws the room
          with bystanders, so the header map unmounts (Set the Stage rides on
          it and is unavailable mid-fight by design; it returns with the map). */}
      {!hasActiveEncounter && <SceneTacticalMap sceneId={id} />}
      <HighlightReel sceneId={id} canGm={scene?.viewer_can_gm} />
      {/* GM "Start Encounter" affordance (#3067), only while no encounter is
          active; the active-encounter controls live in the rail's GM tab. */}
      {!encounterLoading && !hasActiveEncounter && (
        <GMEncounterControls
          sceneId={sceneIdNum}
          encounter={null}
          viewerCanGm={scene?.viewer_can_gm ?? false}
        />
      )}
      {scene && <LinkedStoriesPanel sceneId={id} />}
      {/* #3295 scene check invocation. The call-answer prompt keeps this slot
          when idle; during a fight it renders inline above the accordion. */}
      {isActive && !hasActiveEncounter && (
        <div className="mt-2">
          <CheckCallPromptCard />
        </div>
      )}
      {isActive && (
        <div className="mt-2">
          <SelfCheckPanel />
        </div>
      )}
      {scene?.viewer_can_gm && (
        <div className="mt-2">
          <GMAdjudicationPanel
            scene={scene}
            tabs={hasActiveEncounter ? NON_COMBAT_GM_TOOL_TABS : GM_TOOL_TABS}
          />
        </div>
      )}
    </>
  );

  return (
    <PersonaCardContext.Provider value={cardContextValue}>
      <div className="flex h-full flex-col">
        <div className="shrink-0 px-4 pt-4">
          <SceneHeader scene={scene} onRefresh={() => refetch()} />
          {battle && battle.outcome === 'unresolved' && (
            <Link
              to={`/scenes/${id}/battle`}
              className="mt-1 inline-block text-sm text-blue-600 hover:underline"
              data-testid="scene-battle-map-link"
            >
              Battle Map
            </Link>
          )}
          {battle && battle.outcome !== 'unresolved' && (
            <Link
              to={`/battles/${battle.id}`}
              className="mt-1 inline-block text-sm text-blue-600 hover:underline"
              data-testid="scene-battle-writeup-link"
            >
              Battle Writeup
            </Link>
          )}
          {scene && <RitualProposedChip sceneId={scene.id} />}
          {scene?.is_owner && <PrecapturePanel sceneId={id} />}
          {isActive && <ConsentPrompt sceneId={id} />}
          {isActive && <SineatingInbox />}
          {isActive && <SoulTetherRescuePrompt />}
          {isActive && <EntryFlourishOfferGate characterSheetId={characterSheetId} />}
          {isActive && <WitnessReactionOfferGate personaId={personaId} />}
          {/* #3557 combat layout. Everything above this line either needs an
            answer (consent, sineating, soul-tether, flourish, witness) or is the scene's
            identity; it stays inline in both shapes. Below: while an encounter
            is active the header map yields to the rail's map (one map, with
            bystanders), the pending check-call prompt stays inline, and the
            rest of the stack folds behind one closed "Scene tools" accordion so
            the feed and composer sit under the title. Idle renders today's
            stack unchanged. */}
          {hasActiveEncounter ? (
            <>
              {isActive && (
                <div className="mt-2">
                  <CheckCallPromptCard />
                </div>
              )}
              <Accordion
                type="single"
                collapsible
                className="mt-2"
                data-testid="scene-tools-accordion"
              >
                <AccordionItem value="scene-tools" className="border-b-0">
                  <AccordionTrigger className="py-2 text-sm" data-testid="scene-tools-trigger">
                    Scene tools
                  </AccordionTrigger>
                  <AccordionContent>{sceneTools}</AccordionContent>
                </AccordionItem>
              </Accordion>
            </>
          ) : (
            sceneTools
          )}
        </div>

        {/* Combat rail fold-in (#2197): a two-column C-frame grid while an active
          encounter exists (or the GM story rail is shown); single column
          otherwise. The rail's GM tab (#3557) hosts the encounter controls. */}
        <div
          className={cn(
            'min-h-0 flex-1',
            showCombatRail || showStoryRail
              ? 'grid grid-cols-[1fr_360px] gap-4 px-4 pb-4'
              : 'flex flex-col'
          )}
        >
          <div className="flex min-h-0 flex-1 flex-col" data-testid="scene-detail-left">
            {/* #3565 - the mission scenario a scene's beats run on; self-hides
              for non-participants, so it's mounted for every viewer. */}
            {scene && <ScenarioCard scene={scene} />}

            {/* Main interaction area with threading */}
            <SceneInteractionPanel
              sceneId={id}
              roomName={roomName}
              onComposerModeChange={handleComposerModeChange}
              onAddTarget={setPendingTarget}
              onAttachAction={handleActionAttach}
              canGm={scene?.viewer_can_gm}
              onAvatarClick={setCardPersona}
            />

            {/* GM prompt queue: mechanical events awaiting narration (#4101) */}
            {scene?.viewer_can_gm && <GMPromptQueue sceneId={id} personas={scene.personas ?? []} />}

            {/* Composer + Action Panel */}
            {isActive && (
              <div className="shrink-0">
                {activeCharacter && (
                  <>
                    <PendingActionAttachments
                      sceneId={id}
                      personaId={personaId}
                      detachedIds={detachedActionIds}
                      onDetach={handleDetach}
                      onUndoDetach={handleUndoDetach}
                    />
                    <CommandInput
                      character={activeCharacter}
                      composerMode={composerMode}
                      onModeChange={handleComposerModeChange}
                      targetToAppend={targetToAppend}
                      onTargetConsumed={handleTargetConsumed}
                      sceneId={id}
                      actionAttachment={actionAttachment}
                      onActionAttach={handleActionAttach}
                      onActionDetach={handleActionDetach}
                      onSubmitAction={handleSubmitAction}
                      personaId={personaId}
                      pendingActionIds={pendingActionIds}
                      detachedActionIds={detachedActionIds}
                      onPoseSubmitted={handlePoseSubmitted}
                      isAtPlace={isAtPlace}
                      currentPlaceId={currentPlace?.id ?? null}
                      speakingAs={
                        activeEntry
                          ? {
                              name: activeEntry.name,
                              thumbnailUrl: activeEntry.profile_picture_url,
                            }
                          : undefined
                      }
                      ready={isConnected}
                    />
                  </>
                )}
                <ActionPanel sceneId={id} />
              </div>
            )}
          </div>

          {(showCombatRail || showStoryRail) && (
            <div
              ref={railRef}
              className="min-h-0 space-y-3 overflow-y-auto"
              data-testid="scene-detail-combat-rail"
            >
              {showStoryRail && scene && <GMStoryRail scene={scene} />}
              {showCombatRail && (
                <>
                  {/* The rail lingers past the encounter's end so the aftermath digest
                    and outcome banner stay readable (#3551). */}
                  {encounterChooser}
                  <CombatRail
                    sceneId={sceneIdNum}
                    encounterId={railEncounterId}
                    viewerCanGm={hasActiveEncounter && (scene?.viewer_can_gm ?? false)}
                    scene={scene}
                    onDismissOutcome={handleDismissOutcome}
                  />
                </>
              )}
            </div>
          )}
        </div>
        <CharacterCardDrawer
          persona={cardPersona}
          onClose={() => setCardPersona(null)}
          viewerEntryId={viewerEntryId}
          onWhisper={handleWhisper}
        />
      </div>
    </PersonaCardContext.Provider>
  );
}
